// pages/legal/accept-terms.page.jsx

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthFetch } from "../../services/clerk.service";
import { TOS_TEXT } from "./terms-of-service.page";
import { PRIVACY_TEXT } from "./privacy-policy.page";
import PageBackdrop from "../../components/ui/PageBackdrop";

const APP_NAME = process.env.REACT_APP_NAME || "Max Stock Machine";

const DocViewer = ({ title, content, accepted, onAccept }) => {
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40)
      setScrolled(true);
  };

  return (
    <div className={`at-doc${accepted ? " at-doc-accepted" : ""}`}>
      <div className="at-doc-header">
        <div className="at-doc-title-row">
          <span className="at-doc-label">{title}</span>
          {accepted && (
            <span className="at-accepted-badge">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M2 5l2.5 2.5L8 3"
                  stroke="#34d399"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Accepted
            </span>
          )}
        </div>
        {!accepted && !scrolled && (
          <p className="at-scroll-hint">Scroll to the bottom to accept</p>
        )}
      </div>

      <div className="at-doc-scroll" ref={scrollRef} onScroll={handleScroll}>
        <pre className="at-pre">{content}</pre>
      </div>

      {!accepted && (
        <div className="at-doc-footer">
          <button
            className={`at-accept-btn${scrolled ? " at-accept-btn-ready" : ""}`}
            onClick={onAccept}
            disabled={!scrolled}
          >
            {scrolled
              ? `I accept the ${title}`
              : "Scroll to read before accepting"}
          </button>
        </div>
      )}
    </div>
  );
};

const AcceptTermsPage = ({ onAccepted }) => {
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const bothAccepted = tosAccepted && privacyAccepted;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await authFetch("/users/me/accept-terms", { method: "POST" });
      if (!res.ok) throw new Error("Failed to save acceptance");
      if (onAccepted) await onAccepted();
      navigate("/");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .at-root { background: var(--bg); min-height: 100vh; font-family: var(--font-sans); color: var(--text-primary); position: relative; }
        .at-inner { position: relative; z-index: 1; max-width: 780px; margin: 0 auto; padding: 52px 24px 80px; }

        .at-header { margin-bottom: 36px; opacity: 0; animation: fadeUp 0.45s 0.05s ease forwards; }
        .at-eyebrow { font-family: var(--font-mono); font-size: 12px; color: var(--accent); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
        .at-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
        .at-title { font-family: var(--font-display); font-size: clamp(28px, 4vw, 42px); font-weight: 400; line-height: 1.1; letter-spacing: -0.02em; margin-bottom: 10px; }
        .at-title em { font-style: italic; color: var(--accent); }
        .at-sub { font-size: 15px; color: var(--text-tertiary); line-height: 1.7; font-weight: 400; max-width: 520px; }

        .at-progress { display: flex; align-items: center; gap: 10px; margin-bottom: 32px; opacity: 0; animation: fadeUp 0.45s 0.12s ease forwards; }
        .at-progress-step { display: flex; align-items: center; gap: 6px; font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); letter-spacing: 0.06em; white-space: nowrap; }
        .at-progress-dot { width: 8px; height: 8px; border-radius: 50%; border: 1.5px solid var(--text-tertiary); transition: all 0.3s; flex-shrink: 0; }
        .at-progress-dot.done { background: var(--accent); border-color: var(--accent); }
        .at-progress-dot.active { border-color: var(--accent); }
        .at-progress-line { flex: 1; height: 1px; background: var(--border-subtle); }

        .at-doc { background: var(--surface-1); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); overflow: hidden; margin-bottom: 20px; opacity: 0; animation: fadeUp 0.45s ease forwards; transition: border-color 0.3s; }
        .at-doc:nth-of-type(1) { animation-delay: 0.18s; }
        .at-doc:nth-of-type(2) { animation-delay: 0.26s; }
        .at-doc-accepted { border-color: var(--accent-border); }

        .at-doc-header { padding: 16px 20px; border-bottom: 1px solid var(--border-subtle); }
        .at-doc-title-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
        .at-doc-label { font-family: var(--font-mono); font-size: 12px; color: var(--text-primary); text-transform: uppercase; letter-spacing: 0.1em; font-weight: 500; }
        .at-accepted-badge { display: flex; align-items: center; gap: 5px; font-family: var(--font-mono); font-size: 12px; color: var(--accent); background: var(--accent-soft); border: 1px solid var(--accent-border); border-radius: var(--radius-pill); padding: 3px 10px; letter-spacing: 0.06em; }
        .at-scroll-hint { font-size: 12px; color: var(--text-tertiary); font-weight: 400; }

        .at-doc-scroll { height: 280px; overflow-y: auto; padding: 20px; scrollbar-width: thin; scrollbar-color: var(--border-subtle) transparent; }
        .at-doc-scroll::-webkit-scrollbar { width: 4px; }
        .at-doc-scroll::-webkit-scrollbar-thumb { background: var(--border-subtle); border-radius: 2px; }
        .at-pre { font-family: var(--font-mono); font-size: 13px; color: var(--text-tertiary); line-height: 1.8; white-space: pre-wrap; word-break: break-word; }

        .at-doc-footer { padding: 14px 20px; border-top: 1px solid var(--border-subtle); background: var(--surface-2); }
        .at-accept-btn { width: 100%; padding: 11px 20px; border-radius: var(--radius-sm); border: 1px solid var(--border-subtle); background: transparent; color: var(--text-tertiary); cursor: not-allowed; font-family: var(--font-mono); font-size: 12px; letter-spacing: 0.06em; transition: all 0.2s; }
        .at-accept-btn-ready { background: var(--accent-soft); border-color: var(--accent-border); color: var(--accent); cursor: pointer; }
        .at-accept-btn-ready:hover { background: rgba(52,211,153,0.15); }

        .at-cta { opacity: 0; animation: fadeUp 0.45s 0.32s ease forwards; }
        .at-cta-note { font-size: 13px; color: var(--text-tertiary); font-weight: 400; line-height: 1.6; margin-bottom: 16px; }
        .at-submit-btn { width: 100%; padding: 14px; border-radius: var(--radius-md); border: none; cursor: pointer; font-family: var(--font-sans); font-size: 14px; font-weight: 600; background: var(--accent); color: var(--accent-ink); transition: background var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out), opacity var(--dur-fast) var(--ease-out); letter-spacing: 0.02em; }
        .at-submit-btn:hover:not(:disabled) { background: var(--accent-hover); transform: translateY(-1px); }
        .at-submit-btn:disabled { opacity: 0.35; cursor: not-allowed; transform: none; }
        .at-error { font-size: 13px; color: var(--negative); margin-top: 10px; text-align: center; font-weight: 400; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 500px) { .at-inner { padding: 32px 16px 60px; } .at-doc-scroll { height: 220px; } }
      `}</style>

      <div className="at-root">
        <PageBackdrop />
        <div className="at-inner">
          <div className="at-header">
            <div className="at-eyebrow">
              <span className="at-eyebrow-dot" />
              Before you continue
            </div>
            <h1 className="at-title">
              A few things to <em>agree to.</em>
            </h1>
            <p className="at-sub">
              Please read and accept our Terms of Service and Privacy Policy
              before accessing {APP_NAME}. This only takes a moment.
            </p>
          </div>

          <div className="at-progress">
            <div className="at-progress-step">
              <div
                className={`at-progress-dot${tosAccepted ? " done" : " active"}`}
              />
              Terms of Service
            </div>
            <div className="at-progress-line" />
            <div className="at-progress-step">
              <div
                className={`at-progress-dot${privacyAccepted ? " done" : tosAccepted ? " active" : ""}`}
              />
              Privacy Policy
            </div>
            <div className="at-progress-line" />
            <div className="at-progress-step">
              <div
                className={`at-progress-dot${bothAccepted ? " done" : ""}`}
              />
              Done
            </div>
          </div>

          <DocViewer
            title="Terms of Service"
            content={TOS_TEXT}
            accepted={tosAccepted}
            onAccept={() => setTosAccepted(true)}
          />
          <DocViewer
            title="Privacy Policy"
            content={PRIVACY_TEXT}
            accepted={privacyAccepted}
            onAccept={() => setPrivacyAccepted(true)}
          />

          <div className="at-cta">
            <p className="at-cta-note">
              By clicking below you confirm that you have read and agree to both
              documents. Your acceptance is recorded with a timestamp.
            </p>
            <button
              className="at-submit-btn"
              onClick={handleSubmit}
              disabled={!bothAccepted || submitting}
            >
              {submitting
                ? "Saving…"
                : bothAccepted
                  ? "I agree — take me to the app →"
                  : "Accept both documents above to continue"}
            </button>
            {error && <p className="at-error">{error}</p>}
          </div>
        </div>
      </div>
    </>
  );
};

export default AcceptTermsPage;
